import moment from 'moment';
import get from 'lodash/get';
import uniq from 'lodash/uniq';
import omit from 'lodash/omit';
import forOwn from 'lodash/forOwn';
import keyBy from 'lodash/keyBy';
import extend from 'lodash/extend';
import values from 'lodash/values';
import isArray from 'lodash/isArray';
import last from 'lodash/last';
import cloneDeep from 'lodash/cloneDeep';

import {
  IBakery,
  IProductType,
  IOrderDelivery,
  IUpsellProductType,
  calcDiscountedPrice,
  IProductTypeSizeProduct,
  getMaxQuantityForLineInGroup,
  PRODUCT_TYPE_STRUCTURE_TYPES,
  stringHelper,
} from '@cakeiteasy/web-utils';

import { ICartLine, ICartLineProduct } from '../models/cart/i-cart-line';
import { IAppliedDiscount, ICart, IAdditionalDeliveryInfo } from '../models/cart/i-cart';
import { IReturnPackagingLine } from '../../return-packaging/models/i-return-packaging-details';
import { ICakeCartLine } from '../models/cart/i-cake-cart-line';
import { ISizeExtraGroup, ISizeExtraGroupExtraProduct } from '../models/cart/i-size-extra-group.interface';
import { IApiOrderLine } from '../models/api-response/i-api-multi-order';

import { extendPrintingPicturesWithPrice, extendWithPrice } from '../helpers/price-data-helper';
import { makeCartLineForStoreFromBuilder } from './cart-order-line-store-mappers-helper';
import { canHaveOrderLineBuilderId } from './order-line-builder-id-helper';
import {
  CART_DELIVERY_TYPES,
  CART_LINE_CUSTOM_TYPES,
  CART_LINE_TYPES,
  DELIVERY_ORDER_DELIVERY_TYPES,
  UI_DELIVERY_OPTIONS,
} from '../constants/common';

export interface IUpsellCartLinesGroup {
  lines: { [productId: number]: ICakeCartLine };
  addonsLine: Pick<ICakeCartLine, 'size' | 'imagesForPrinting' | 'questionsMap' | 'extraProductsMap'> | null;
}

export interface IUpsellCartLinesByProductTypeId {
  [productTypeId: number]: IUpsellCartLinesGroup;
}

export function getActualDeliveryPrice({
  price,
  externalDeliveryFee,
  deliveryType,
}: {
  price?: number;
  externalDeliveryFee?: number;
  deliveryType?: string; //DELIVERY_ORDER_DELIVERY_TYPES
}): number {
  const { THIRD_PARTY } = DELIVERY_ORDER_DELIVERY_TYPES;
  return deliveryType === THIRD_PARTY ? externalDeliveryFee : price;
}

export function getCakeCartLineTotalPrice(params: {
  line: ICakeCartLine;
  discount?: number;
  isPurePrice?: boolean;
}): number {
  const { line, discount = null, isPurePrice = false } = params;

  if (line && line.isCustom) {
    return line.price;
  }

  if (!line || !line.size || !line.variant) {
    return 0;
  }

  const _price = line.price || 0;
  let price = discount ? calcDiscountedPrice(_price, discount) : _price;
  let greetingCardPrice = 0;
  let depositPrice = 0;

  const { imagesForPrinting, greetingCard, questionsMap, extraProductsMap, deposit } = line;

  if (greetingCard && greetingCard.price) {
    greetingCardPrice = greetingCard.price;
  }

  if (deposit && deposit.price) {
    depositPrice += deposit.price;
  }

  const countablePrice = getAddOnsPrice({
    imagesForPrinting,
    questionsMap,
    extraProductsMap,
  });

  const persistentPrice = getAddOnsPrice({
    imagesForPrinting,
    questionsMap,
    extraProductsMap,
    forApplyOnce: true,
  });

  const count = isPurePrice ? 1 : line.count;

  // only one greeting card per cake
  // and it could be only one deposit per cake
  return (price + countablePrice) * count + persistentPrice + greetingCardPrice + depositPrice;
}

export const getLastCartLine = (lines: ICakeCartLine[]): ICakeCartLine => {
  return last((lines || []).filter(({ productType }) => productType));
};

export const getActualLinesFromEvent = ({
  line,
  lines,
  upsellLines,
  applyOnceLines,
}: {
  line?: ICakeCartLine;
  lines?: ICakeCartLine[];
  upsellLines?: ICakeCartLine[];
  applyOnceLines?: ICakeCartLine[];
}): ICakeCartLine[] => {
  let data = { line, lines, upsellLines, applyOnceLines };

  return Object.keys(data)
    .filter((key) => Boolean(data[key]))
    .map((key) => (Array.isArray(data[key]) ? data[key] : [data[key]]))
    .reduce((acc, val) => (acc.push(...val), acc), []);
};

const getAddOnsPrice = (params: {
  imagesForPrinting: any;
  questionsMap: any;
  extraProductsMap: { [groupId: number]: any[] };
  forApplyOnce?: boolean;
}): number => {
  const { imagesForPrinting = [], questionsMap = {}, extraProductsMap = {}, forApplyOnce = false } = params;
  const questions = values(questionsMap).filter(({ price, answer }) => price && answer);
  const extraProducts: any[] = values(extraProductsMap).reduce(
    (acc: any[], products: any[]) => (acc.push(...products), acc),
    [],
  );

  return [...imagesForPrinting, ...questions, ...extraProducts]
    .filter(({ applyOnce }) => (forApplyOnce ? applyOnce : !applyOnce))
    .reduce((acc, { price }) => ((acc += price), acc), 0);
};

export function getPiecesMultipleVariantsPriceAndQuantity(
  cartLinesById: { [key: number]: ICakeCartLine },
  discount = null,
): { total: number; quantity: number } {
  let total = 0;
  let quantity = 0;

  /**While all addons for all orderLines will be the same it is necessary any cart line to calculate addons price*/
  const firstCartLine = values(cartLinesById)[0];

  if (!firstCartLine) {
    return { total, quantity };
  }

  const { imagesForPrinting, questionsMap, extraProductsMap, deposit } = firstCartLine;

  const cartLineDeposit = deposit && deposit.id ? deposit.price : 0;
  const countablePrice = getAddOnsPrice({
    imagesForPrinting,
    questionsMap,
    extraProductsMap,
  });
  const persistentPrice = getAddOnsPrice({
    imagesForPrinting,
    questionsMap,
    extraProductsMap,
    forApplyOnce: true,
  });

  forOwn(cartLinesById, (cartLine) => {
    let productPrice = discount ? calcDiscountedPrice(cartLine.price, discount) : cartLine.price;

    quantity += cartLine.count;
    total += cartLine.count ? (productPrice + countablePrice) * cartLine.count : 0;
  });
  total += total ? cartLineDeposit + persistentPrice : 0;

  return { total, quantity };
}

export function getUpsellPriceAndQuantity(cartLinesById: { [key: number]: ICakeCartLine }): {
  total: number;
  quantity: number;
} {
  let total = 0;
  let quantity = 0;

  forOwn(cartLinesById, (cartLine) => {
    quantity += cartLine.count;
    total += cartLine.count > 0 ? getCakeCartLineTotalPrice({ line: cartLine }) : 0;
  });

  return { total, quantity };
}

export function getUpsellLineTotalPrice(params: {
  lines: { [productId: number]: ICakeCartLine };
  addonsLine: Pick<ICakeCartLine, 'imagesForPrinting' | 'questionsMap' | 'extraProductsMap'> | null;
  discount?: number;
  isPurePrice?: boolean;
}): number {
  const { lines, discount = null, isPurePrice = false } = params;
  const addonsLine = params.addonsLine || {};
  const selectedLines = values(lines || {}).filter(({ count }) => count);
  const count = isPurePrice ? 1 : selectedLines.reduce((acc, line) => acc + line.count, 0);

  if (!count) {
    return 0;
  }

  const productsPrice = selectedLines.reduce((acc, line) => {
    const productPrice = discount ? calcDiscountedPrice(line.price || 0, discount) : line.price || 0;
    return acc + productPrice * (isPurePrice ? 1 : line.count);
  }, 0);

  const { imagesForPrinting, questionsMap, extraProductsMap } = addonsLine;
  const countablePrice = getAddOnsPrice({
    imagesForPrinting,
    questionsMap,
    extraProductsMap,
  });
  const persistentPrice = getAddOnsPrice({
    imagesForPrinting,
    questionsMap,
    extraProductsMap,
    forApplyOnce: true,
  });

  return productsPrice + countablePrice * count + persistentPrice;
}

export function getUpsellGroupedPriceAndQuantity(groups: IUpsellCartLinesByProductTypeId): {
  total: number;
  quantity: number;
} {
  return values(groups || {}).reduce(
    (acc, { lines, addonsLine }) => {
      acc.quantity += values(lines || {}).reduce((quantity, line) => quantity + line.count, 0);
      acc.total += getUpsellLineTotalPrice({ lines, addonsLine });
      return acc;
    },
    { total: 0, quantity: 0 },
  );
}

export function getUpsellCartLinesByProductTypeId(params: {
  upsellProductTypes: IUpsellProductType[];
  upsellCartLines?: ICakeCartLine[];
  parentType?: number;
}): IUpsellCartLinesByProductTypeId {
  const { upsellProductTypes = [], upsellCartLines = [], parentType } = params;
  const existingLinesByProductId = keyBy(upsellCartLines, 'productId');
  return upsellProductTypes.reduce((acc, upsellProductType) => {
    const [size] = upsellProductType.sizes;

    acc[upsellProductType.id] = {
      lines: _makeUpsellGroupLines({ upsellProductType, existingLinesByProductId, parentType }),
      addonsLine: _makeUpsellAddonsLine({ size, existingLinesByProductId }),
    };
    return acc;
  }, {});
}

function _makeUpsellGroupLines(params: {
  upsellProductType: IUpsellProductType;
  existingLinesByProductId: { [productId: number]: ICakeCartLine };
  parentType?: number;
}): { [productId: number]: ICakeCartLine } {
  const { upsellProductType, existingLinesByProductId, parentType } = params;
  const [size] = upsellProductType.sizes;

  return size.products.reduce((productAcc, product) => {
    const existingLine = existingLinesByProductId[product.id];

    if (existingLine) {
      productAcc[product.id] = existingLine;
      return productAcc;
    }

    const newCartLine = {
      ...getEmptyCartLine(upsellProductType, null),
      size,
      productId: product.id,
      count: 0,
      variant: product.variant,
      productType: upsellProductType,
      discount: upsellProductType.discount,
      parentType,
    };

    productAcc[product.id] = makeCartLineForStoreFromBuilder(newCartLine as ICakeCartLine, upsellProductType);
    return productAcc;
  }, {});
}

function _makeUpsellAddonsLine(params: {
  size: any;
  existingLinesByProductId: { [productId: number]: ICakeCartLine };
}): IUpsellCartLinesGroup['addonsLine'] {
  const { size, existingLinesByProductId } = params;
  const existingGroupLines = size.products
    .map((product) => existingLinesByProductId[product.id])
    .filter((line) => Boolean(line));
  const sourceLine = existingGroupLines.find(_hasAnyAddons) || existingGroupLines[0];
  const extraProductsMap = sourceLine ? sourceLine.extraProductsMap || {} : _getPreselectedExtraProductsMap(size);

  return {
    size,
    questionsMap: sourceLine?.questionsMap || {},
    extraProductsMap,
    imagesForPrinting: sourceLine?.imagesForPrinting || [],
  };
}

// only for upsells not yet in the cart: a restored line omits groups the user left empty
function _getPreselectedExtraProductsMap(size: any): { [groupId: number]: ISizeExtraGroupExtraProduct[] } {
  return (size.extraGroups || []).reduce((acc, group: ISizeExtraGroup) => {
    const [firstProduct] = group.extraProducts || [];

    if (group.preselectFirstChoice && group.type !== 'YES_NO' && firstProduct) {
      acc[group.id] = [firstProduct];
    }
    return acc;
  }, {});
}

function _hasAnyAddons(line: ICakeCartLine): boolean {
  if (!line) {
    return false;
  }

  const hasPrinting = (line.imagesForPrinting || []).length > 0;
  const hasExtraProducts = Object.keys(line.extraProductsMap || {}).some((groupId) => {
    const selected = line.extraProductsMap[groupId] || [];
    return selected.length > 0;
  });
  const hasQuestions = Object.keys(line.questionsMap || {}).some((questionId) => {
    const question = line.questionsMap[questionId];
    return Boolean(question && question.answer);
  });

  return hasPrinting || hasExtraProducts || hasQuestions;
}

export function getQuantityFromOrderLines(orderLines: ICartLine[]) {
  return orderLines.reduce((result, line) => {
    return result + line.count;
  }, 0);
}

export function getDiscountedAmount(price: number, discount: number) {
  return price * (discount / 100);
}

export function getTotalWithoutDiscount(price: number, discount: number) {
  return price * ((discount + 100) / 100);
}

export function getCartStateWithUpdatedPrices(cart: ICart): ICart {
  cart = cloneDeep(cart);
  cart.lines = cart.lines.map((l) => ({ ...l, fullPrice: getCakeCartLineTotalPrice({ line: l as ICakeCartLine }) }));
  const { appliedDiscount, delivery, invoiceFee, returnPackaging } = cart;
  const deliveryPrice = (delivery && getActualDeliveryPrice(delivery)) || 0;
  const returnPrice = (returnPackaging && returnPackaging.price) || 0;
  const invoiceFeePrice = invoiceFee || 0;
  const totalOrderLinesPrice = _getCartLinesFullPrice(cart.lines);

  let discountedPrice = 0;
  if (appliedDiscount && appliedDiscount.value) {
    discountedPrice = getDiscountedAmount(totalOrderLinesPrice, appliedDiscount.value);
  }

  cart.discountedPrice = discountedPrice;
  cart.totalPrice = totalOrderLinesPrice + deliveryPrice + returnPrice + invoiceFeePrice - cart.discountedPrice;
  return cart;
}

function _getCartLinesFullPrice(lines: ICartLine[]): number {
  return lines.reduce((acc, line) => acc + line.fullPrice, 0);
}

export function getEmptyCartLine(
  productType: IProductType | IUpsellProductType,
  bakeryId?: number,
  params = {},
): ICakeCartLine {
  return {
    bakeryId,
    count: productType.structureType === PRODUCT_TYPE_STRUCTURE_TYPES.CATERING_MENU ? 0 : 1,
    id: null,
    productId: null,
    type: CART_LINE_TYPES.PRODUCT,
    productTypeId: productType.id,
    productType,
    questionsMap: {},
    extraProductsMap: {},
    deposit: {},
    ...params,
  };
}

export function makeSizeProductForOrderLine(
  productType: IProductType | IUpsellProductType,
  product: IProductTypeSizeProduct,
): ICartLineProduct {
  const { id, picture, image, increment, maxQuantity, minQuantity, name, stockSettings, structureType } = productType;
  return {
    ...product,
    type: {
      id,
      picture,
      image,
      increment,
      maxQuantity,
      minQuantity,
      name,
      stockSettings,
      structureType,
    },
  };
}

export function getDiscountFromOrderLines(orderLines): IAppliedDiscount {
  const lines = orderLines
    .filter(({ type }) => type === CART_LINE_TYPES.DISCOUNT)
    .map(({ details: { code, type, value } }) => ({ code, type, value }));

  return lines[0] || null;
}

export function mapDeliveryOrderFromCartResponse(
  deliveryOrder: IOrderDelivery,
  bakery?: IBakery,
  options?: { deliveryPrice: number; customDeliveryPriceEnabled: boolean },
) {
  if (!deliveryOrder) {
    return null;
  }

  const {
    type,
    deliveryType,
    deliveryDate,
    outlet,
    priceWithoutVat,
    externalDeliveryFee,
    id: orderId,
    deliveryTime: timeRange,
    deliveryPhone: phone,
    deliveryCity: cityName,
    deliveryPostNumber: postNumber,
    deliveryAddress: address,
    deliveryAgent: deliveryAgentId,
    deliveryUserName: userName,
    deliveryNote: note,
  } = deliveryOrder;

  let { price } = deliveryOrder;
  const { PICKUP, DELIVERY } = CART_DELIVERY_TYPES;
  const { MAIL, GIFT, THIRD_PARTY } = DELIVERY_ORDER_DELIVERY_TYPES;

  if (
    options &&
    options.customDeliveryPriceEnabled &&
    type === DELIVERY &&
    deliveryType !== THIRD_PARTY &&
    deliveryType !== MAIL
  ) {
    price = options.deliveryPrice || 0;
  }

  const date = deliveryDate ? moment(deliveryDate, 'YYYY-MM-DD').toDate() : null;
  const delivery = {
    orderId,
    type,
    deliveryType,
    date,
    timeRange,
    phone,
    cityName,
    postNumber,
    address,
    externalDeliveryFee,
  } as any;

  const result = {
    [PICKUP]: {
      ...delivery,
      outlet,
      price,
      priceWithoutVat,
      ...(deliveryOrder.outlet && { outletId: deliveryOrder.outlet.id, outletName: deliveryOrder.outlet.name }),
    },
    [DELIVERY]: {
      ...delivery,
      deliveryAgentId,
      userName,
      note,
      priceWithoutVat,
      price,
    },
    [MAIL]: {
      ...delivery,
      userName,
      price,
      priceWithoutVat,
      date: null,
      timeRange: null,
      phone: null,
    },
    [GIFT]: {
      ...deliveryOrder,
      deliveryDate: date,
      orderId,
      price: type === DELIVERY && bakery ? bakery.deliveryDeposit : 0,
    },
  };

  const key = (deliveryType === MAIL && MAIL) || (deliveryType === GIFT && GIFT) || type;
  return result[key];
}

export function defineDeliveryType(delivery: IOrderDelivery): UI_DELIVERY_OPTIONS {
  if (!delivery) {
    return null;
  }

  const { type, deliveryType } = delivery;
  const { MAIL, GIFT } = UI_DELIVERY_OPTIONS;

  if (deliveryType && (deliveryType === MAIL || deliveryType === GIFT)) {
    return deliveryType;
  }

  return type as UI_DELIVERY_OPTIONS;
}

export function mapBakeryOrderOrderLinesToCartLines(
  bakeryOrderOrderLines: IApiOrderLine[],
  options: { bakeryId: number; priceReducePercent: number },
): ICakeCartLine[] {
  const { bakeryId, priceReducePercent } = options;

  // TODO Improve logic so user is aware of such products
  bakeryOrderOrderLines = bakeryOrderOrderLines.filter(
    ({ isCustom, product, details, type }) =>
      (!isCustom && product !== null) ||
      (isCustom && ((details && details.applyOnce) || type === CART_LINE_TYPES.PRODUCT)),
  );

  if (priceReducePercent) {
    bakeryOrderOrderLines = bakeryOrderOrderLines.map((orderLine) =>
      reduceOrderLinePrices(orderLine, priceReducePercent)
    );
  }

  return bakeryOrderOrderLines.map((orderLine) => mapOrderLineFromCartResponse(orderLine, bakeryId));
}

/**The RETURN_PACKAGING line carries no product, so mapBakeryOrderOrderLinesToCartLines drops it.
 * It is lifted onto the cart as its own field instead, the way the delivery order already is.*/
export function extractReturnPackagingLine(bakeryOrderOrderLines: IApiOrderLine[]): IReturnPackagingLine {
  const line = (bakeryOrderOrderLines || []).find(({ type }) => type === CART_LINE_TYPES.RETURN_PACKAGING);

  if (!line || !line.returnPackaging) {
    return null;
  }

  const { id, clientId, price, priceWithoutVat, returnPackaging } = line;
  return { id, clientId, price, priceWithoutVat, returnPackaging };
}

export function reduceOrderLinePrices(orderLine: IApiOrderLine, priceReducePercent: number): IApiOrderLine {
  const { orderLines } = orderLine;

  return {
    ...orderLine,
    price: _getOrderLinePrice(orderLine, priceReducePercent),
    orderLines: orderLines.map((line) => ({
      ...line,
      price: calcDiscountedPrice(line.price, priceReducePercent),
    })),
  };
}

function _getOrderLinePrice(orderLine: IApiOrderLine, priceReducePercent: number): number {
  const {
    price,
    product: {
      hasCustomPrice,
      size: { price: sizePrice },
      type: { structureType },
    },
  } = orderLine;
  const { PIECES_GROUP, PIECES } = PRODUCT_TYPE_STRUCTURE_TYPES;
  const isPieces = structureType === PIECES || structureType === PIECES_GROUP;

  return hasCustomPrice
    ? isPieces
      ? price
      : sizePrice + calcDiscountedPrice(price - sizePrice, priceReducePercent)
    : calcDiscountedPrice(price, priceReducePercent);
}

export function mapOrderLineFromCartResponse(orderLine: any, bakeryId: number) {
  const { details, type, isCustom } = orderLine;

  if ((details && details.applyOnce) || (isCustom && type === CART_LINE_TYPES.PRODUCT)) {
    return {
      ...orderLine,
      bakeryId,
      count: orderLine.quantity,
    };
  }

  const imagesForPrinting = orderLine.orderLines
    .filter((o) => o.type === CART_LINE_TYPES.PICTURE)
    .map(({ printingPicture, details, price }) => ({
      ...printingPicture,
      ...details,
      price,
    }));

  const deposit = orderLine.orderLines.find((o) => o.type === CART_LINE_TYPES.DEPOSIT) || {};

  const questionsMap = orderLine.orderLines
    .filter((o) => o.type === CART_LINE_TYPES.QUESTION)
    .map((q) =>
      extendWithPrice(q, {
        answer: stringHelper.replaceBrsWithTextBreaks(get(q.details, 'answer')),
      }),
    )
    .reduce((acc, q) => ((acc[q.question] = q), acc), {});

  let greetingCard = orderLine.orderLines.find((o) => o.type === CART_LINE_TYPES.GREETING_CARD);
  if (greetingCard) {
    /** greetingCard.checked is checked if greetingCard.text isn't empty, and greetingCard.checked is set in cake-builder.component
     * But it doesn't exist in server response, so for saving greetingCard when change count in cart it is necessary to set it*/
    greetingCard = {
      checked: true,
      ...greetingCard,
      text: stringHelper.replaceBrsWithTextBreaks(greetingCard.details.text),
    };
  }

  const extraProducts = orderLine.orderLines.filter((o) => o.type === CART_LINE_TYPES.EXTRA_PRODUCT);
  const extraProductsMap = {};

  extraProducts.forEach((extraProduct) => {
    extraProduct.discountAmount = extraProduct.discountAmount || 0;
    const { product } = extraProduct;

    if (product) {
      const groupId = extraProduct.product.extraGroup.id;

      if (isArray(extraProductsMap[groupId])) {
        extraProductsMap[groupId].push(extendWithPrice(extraProduct.product, extraProduct));
      } else {
        extraProductsMap[groupId] = [extendWithPrice(extraProduct.product, extraProduct)];
      }
    } else {
      extraProductsMap[CART_LINE_CUSTOM_TYPES.CUSTOM_PRODUCTS]
        ? extraProductsMap[CART_LINE_CUSTOM_TYPES.CUSTOM_PRODUCTS].push(extraProduct)
        : (extraProductsMap[CART_LINE_CUSTOM_TYPES.CUSTOM_PRODUCTS] = [extraProduct]);
    }
  });

  const cakeModel: ICakeCartLine = {
    bakeryId,
    id: orderLine.id,
    type: CART_LINE_TYPES.PRODUCT,
    count: orderLine.quantity,
    productId: orderLine.product.id,
    size: { details: orderLine.product.size },
    productType: orderLine.product.type,
    variant: orderLine.product.variant,
    orderLineBuilderId: orderLine.orderLineBuilderId,
    imagesForPrinting,
    questionsMap,
    greetingCard,
    extraProductsMap,
    deposit,
  };

  extendPrintingPicturesWithPrice(cakeModel.imagesForPrinting);
  extendWithPrice(cakeModel.greetingCard, greetingCard);
  extendWithPrice(cakeModel.deposit, deposit);
  extendWithPrice(cakeModel, omit(orderLine.product, 'name'), orderLine);

  return cakeModel;
}

export function transformToServerCartLine(line: ICakeCartLine) {
  if (line.details && line.details.applyOnce) {
    return transformApplyOnceLines(line);
  }

  return transformNormalLines(line);
}

const transformApplyOnceLines = (cartLine: ICakeCartLine) => {
  const { clientId, isCustom, bakeryId: bakery, count: quantity, details, name, price } = cartLine;
  return {
    clientId,
    quantity,
    details,
    name,
    price,
    isCustom,
    bakery,
    product: null,
    orderLines: [],
    type: CART_LINE_TYPES.PRODUCT,
  };
};

const transformNormalLines = (line: ICakeCartLine) => {
  if (!line.productId) {
    return null;
  }

  const orderLines = values(line.extraProductsMap)
    .reduce((res, arr) => res.concat(arr), [])
    .filter((product) => product.id !== 0)
    .map((value) => {
      const ext = value;
      if (ext.id !== 0) {
        return {
          type: CART_LINE_TYPES.EXTRA_PRODUCT,
          product: ext.id,
        };
      }
    });

  const questionOrderLines = values(line.questionsMap)
    .filter(({ answer }) => Boolean(answer))
    .map(({ question, answer }) => ({
      question, // id of question
      type: CART_LINE_TYPES.QUESTION,
      answer: stringHelper.replaceTextBreaksWithBrs(answer).trim(),
    }));

  orderLines.push(...questionOrderLines);

  if (line.imagesForPrinting && line.imagesForPrinting.length) {
    line.imagesForPrinting.forEach(({ id: printingPicture, url }) => {
      orderLines.push({
        printingPicture,
        url,
        type: CART_LINE_TYPES.PICTURE,
      });
    });
  }

  if (line.greetingCard && line.greetingCard.checked && line.greetingCard.text) {
    orderLines.push({
      type: CART_LINE_TYPES.GREETING_CARD,
      text: stringHelper.replaceTextBreaksWithBrs(line.greetingCard.text).trim(),
    });
  }

  if (line.deposit && line.deposit.deposit) {
    orderLines.push({
      type: CART_LINE_TYPES.DEPOSIT,
      deposit: line.deposit.deposit,
    });
  }

  const cartLine = {
    orderLineId: line.id,
    clientId: line.clientId,
    orderLineBuilderId: line.orderLineBuilderId,
    product: line.productId,
    quantity: line.count,
    parentType: line.parentType || null,
    orderLines: orderLines.filter((l) => Boolean(l)),
  };

  if (line.source) {
    extend(cartLine, { source: line.source });
  }

  if (line.sourceMeta) {
    extend(cartLine, { sourceMeta: line.sourceMeta });
  }

  return cartLine;
};

function getMinQuantityForOrderLine(line: ICakeCartLine, lines: ICakeCartLine[]): number {
  if (line.size.details.pieces) {
    const groupOfLines = getGroupOfLines(line, lines);

    if (groupOfLines.length > 1 && multipleVariantsCheck(groupOfLines)) {
      const groupQuantity = groupOfLines.reduce((total, l) => total + l.count, 0);
      // so the minQuantity could be less than existing on the extra we have on the group
      return Math.max(0, line.count - (groupQuantity - line.productType.minQuantity));
    }

    return line.productType.minQuantity || 1;
  }

  return 1;
}

function getMaxQuantityForOrderLine(line: ICakeCartLine, lines: ICakeCartLine[]): number | null {
  if (!line.productType) {
    return null;
  }

  const groupQuantity = getGroupOfLines(line, lines).reduce((total, l) => total + l.count, 0);

  return getMaxQuantityForLineInGroup(line.productType, line.count, groupQuantity);
}

function getGroupOfLines(line: ICakeCartLine, lines: ICakeCartLine[]): ICakeCartLine[] {
  const builderId = line.orderLineBuilderId;

  if (!builderId) {
    return [];
  }

  return lines.filter(
    (groupLine) =>
      groupLine.orderLineBuilderId === builderId &&
      (!line.parentType || groupLine.productType?.id === line.productType?.id),
  );
}

function multipleVariantsCheck(groupOfLines: ICakeCartLine[]): boolean {
  // check if number of different variants is bigger than 1
  return uniq(groupOfLines.map((l) => (l as ICakeCartLine).variant.id)).length > 1;
}

function getOrderLinesTotalPrice(lines: ICakeCartLine[]): number {
  let totalPrice = 0;
  lines.forEach((line) => (totalPrice += getCakeCartLineTotalPrice({ line })));
  return totalPrice;
}

function prepareDataFromDeliveryInfoUpdatePayload(payload: IAdditionalDeliveryInfo, cart) {
  const {
    freeDeliveryEnabled,
    freeDeliveryAllowsOnlyForCompanies,
    minCartPriceAllowsFreeDelivery,
    customDeliveryPriceEnabled,
    deliveryPrice,
    newDeliveryPrice,
  } = initDeliveryInfoPayload(payload, cart);
  let cartDeliveryPrice =
    newDeliveryPrice || newDeliveryPrice === 0 ? newDeliveryPrice : cart.delivery && cart.delivery.price;

  const { DELIVERY, MAIL } = UI_DELIVERY_OPTIONS;
  if (
    customDeliveryPriceEnabled &&
    cart.delivery &&
    cart.delivery.type === DELIVERY &&
    cart.delivery.deliveryType !== MAIL
  ) {
    cartDeliveryPrice = deliveryPrice || 0;
  } else {
    const freeDeliveryCanBeApplied =
      freeDeliveryEnabled &&
      (!freeDeliveryAllowsOnlyForCompanies || (freeDeliveryAllowsOnlyForCompanies && cart.company));

    let priceForChecking = 0;
    if (freeDeliveryCanBeApplied) {
      priceForChecking = cartHelper.getOrderLinesTotalPrice(cart.lines);
    }

    cartDeliveryPrice =
      freeDeliveryCanBeApplied && priceForChecking >= minCartPriceAllowsFreeDelivery ? 0 : cartDeliveryPrice;
  }

  const delivery = cart.lines.length > 0 ? { ...cart.delivery, price: cartDeliveryPrice } : null;

  return {
    delivery,
    freeDeliveryEnabled,
    freeDeliveryAllowsOnlyForCompanies,
    minCartPriceAllowsFreeDelivery,
    customDeliveryPriceEnabled,
    deliveryPrice,
  };
}

function initDeliveryInfoPayload(payload: IAdditionalDeliveryInfo, cart) {
  let {
    freeDeliveryEnabled = null,
    freeDeliveryAllowsOnlyForCompanies = null,
    minCartPriceAllowsFreeDelivery = null,
    customDeliveryPriceEnabled = null,
    deliveryPrice = null,
    newDeliveryPrice = null,
  } = payload;

  customDeliveryPriceEnabled =
    customDeliveryPriceEnabled ||
    (cart.additionalDeliveryInfo && cart.additionalDeliveryInfo.customDeliveryPriceEnabled);

  deliveryPrice = deliveryPrice || (cart.additionalDeliveryInfo && cart.additionalDeliveryInfo.deliveryPrice);

  freeDeliveryEnabled =
    freeDeliveryEnabled || (cart.additionalDeliveryInfo && cart.additionalDeliveryInfo.freeDeliveryEnabled);

  freeDeliveryAllowsOnlyForCompanies =
    freeDeliveryAllowsOnlyForCompanies ||
    (cart.additionalDeliveryInfo && cart.additionalDeliveryInfo.freeDeliveryAllowsOnlyForCompanies);

  minCartPriceAllowsFreeDelivery =
    minCartPriceAllowsFreeDelivery ||
    (cart.additionalDeliveryInfo && cart.additionalDeliveryInfo.minCartPriceAllowsFreeDelivery);

  return {
    freeDeliveryEnabled,
    freeDeliveryAllowsOnlyForCompanies,
    minCartPriceAllowsFreeDelivery,
    customDeliveryPriceEnabled,
    deliveryPrice,
    newDeliveryPrice,
  };
}

function getDeliveryPriceAfterLinesUpdate(cart: ICart, lines: ICakeCartLine[] = null): number {
  const { additionalDeliveryInfo, delivery, company } = cart;
  const cartDeliveryPrice = (delivery && delivery.price) || 0;

  if (!additionalDeliveryInfo) {
    return cartDeliveryPrice;
  }

  const {
    freeDeliveryEnabled,
    freeDeliveryAllowsOnlyForCompanies,
    minCartPriceAllowsFreeDelivery,
    customDeliveryPriceEnabled,
    deliveryPrice,
  } = additionalDeliveryInfo;

  const { DELIVERY, MAIL } = UI_DELIVERY_OPTIONS;
  if (delivery && delivery.type === DELIVERY && delivery.deliveryType !== MAIL && customDeliveryPriceEnabled) {
    return deliveryPrice || 0;
  }

  const priceForChecking = cartHelper.getOrderLinesTotalPrice(lines || cart.lines);
  if (freeDeliveryEnabled && priceForChecking >= minCartPriceAllowsFreeDelivery) {
    return !freeDeliveryAllowsOnlyForCompanies || (freeDeliveryAllowsOnlyForCompanies && company)
      ? 0
      : cartDeliveryPrice;
  }

  return cartDeliveryPrice;
}

export const cartHelper = {
  getCakeCartLineTotalPrice,
  getPiecesMultipleVariantsPriceAndQuantity,
  getQuantityFromOrderLines,
  getDiscountedAmount,
  getTotalWithoutDiscount,
  getCartStateWithUpdatedPrices,
  getEmptyCartLine,
  mapDeliveryOrderFromCartResponse,
  mapBakeryOrderOrderLinesToCartLines,
  extractReturnPackagingLine,
  mapOrderLineFromCartResponse,
  transformToServerCartLine,

  getMinQuantityForOrderLine,
  getMaxQuantityForOrderLine,
  getGroupOfLines,
  getOrderLinesTotalPrice,
  prepareDataFromDeliveryInfoUpdatePayload,
  getDeliveryPriceAfterLinesUpdate,
  defineDeliveryType,
  getDiscountFromOrderLines,
  canHaveOrderLineBuilderId,
};
